import { Middleware } from 'redux';
import { isAction } from '@reduxjs/toolkit';

import { getLoggedInUsersOrganizationMaterialSet } from '@paragon/material-lists';
import { enqueuePermanentWarning, enqueueSnackbar } from '@paragon/notification-tools';

import { RootState } from '..';
import { ReduxDispatch } from '../actions/thunkActions';

export const deprecationWatchdogMiddleware: Middleware<{}, RootState, ReduxDispatch> =
  (store) => (next) => (action) => {
    if (!isAction(action)) {
      return next(action);
    }

    const result = next(action);

    // We dispatch this action below, so prevent infinite recursion.
    if (enqueueSnackbar.match(action)) return result;

    const state = store.getState();

    const componentDesigns = Object.values(state.componentDesigns.data);
    const organizationMaterialSet = getLoggedInUsersOrganizationMaterialSet(state);

    if (componentDesigns.length === 0 || organizationMaterialSet == null) return result;

    const componentDesignsUsingArchivedMaterialList = new Set<string>();

    for (const componentDesign of componentDesigns) {
      const lumberListGuid = componentDesign.materialSelection?.lumberListGuid;
      const lumberList = lumberListGuid ? organizationMaterialSet.lumberLists[lumberListGuid] : null;

      const plateListGuid = componentDesign.materialSelection?.plateListGuid;
      const plateList = plateListGuid ? organizationMaterialSet.plateLists[plateListGuid] : null;

      if (lumberList?.isArchived) {
        componentDesignsUsingArchivedMaterialList.add(componentDesign.component.name);
      }

      if (plateList?.isArchived) {
        componentDesignsUsingArchivedMaterialList.add(componentDesign.component.name);
      }
    }

    if (componentDesignsUsingArchivedMaterialList.size > 5) {
      store.dispatch(enqueuePermanentWarning('Many component designs are using an archived material list.'));
    } else if (componentDesignsUsingArchivedMaterialList.size === 1) {
      store.dispatch(
        enqueuePermanentWarning(
          `${componentDesignsUsingArchivedMaterialList.values().next().value} uses an archived material list.`,
        ),
      );
    } else if (componentDesignsUsingArchivedMaterialList.size > 0) {
      store.dispatch(
        enqueuePermanentWarning(
          `Some component designs use an archived material list: ${Array.from(
            componentDesignsUsingArchivedMaterialList,
          ).join(', ')}`,
        ),
      );
    }

    return result;
  };
